import { Text, useUpdateEffect } from "@chakra-ui/react";
import {
  ForwardedRef,
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer, { ReactPlayerProps } from "react-player";

type Props = ReactPlayerProps;

export type VideoPlayerHandle = {
  videoPlayer?: ReactPlayer | null;
};

const VideoPlayer = (
  { onError, url, ...rest }: Props,
  ref: ForwardedRef<VideoPlayerHandle>,
) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState<boolean>(false);
  const videoRef = useRef<ReactPlayer>(null);

  useImperativeHandle(ref, () => ({
    videoPlayer: videoRef.current,
  }));

  const onPlayerError: (e: unknown) => void = useCallback(
    (e) => {
      setHasError(true);
      onError?.(e);
    },
    [onError],
  );

  useUpdateEffect(() => {
    setHasError(false);
  }, [url]);

  if (hasError) return <Text>{t("common.labels.unable_to_load_video")}</Text>;

  return (
    <ReactPlayer
      ref={videoRef}
      width="100%"
      controls
      url={url}
      {...rest}
      onError={onPlayerError}
    />
  );
};

export default forwardRef(VideoPlayer) as (
  props: Props & { ref?: Ref<VideoPlayerHandle> },
) => JSX.Element;
